'use client';

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';

import { Button, Modal } from '@/components';
import {
  TourneyList,
  MainHome,
  MainBox,
} from '@/components/GenericsTournaments';
import ModalTkred from '@/components/ModalTkred';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import useToast from '@/hooks/useToast';
import useTourney from '@/hooks/useTourney';
import ROUTES from '@/router/routes';
import {
  ActionTypeManageTDAEnum,
  getTdaBasicDataById,
  requestPostulationTda,
  postManagePostulant,
} from '@/services/tda';
import type {
  ActionsTourneys,
  GenericItemTourneysData,
  ParticipandoTda,
  PendientesTda,
  TorneoAnterior,
} from '@/types/tda';
import { EnumContextGDT, EnumContextTourneys } from '@/types/team';
import {
  cleanStorageRedirectInvitation,
  formatearNumero,
  STATUS_TOURNEY_INVITATION,
  TEXTS,
} from '@/utils';

interface TournamentDetails {
  estado: boolean;
  nombre: string;
  nombreDuenio: string;
  apellidoDuenio: string;
  fechaInicio: {
    nombre: string;
  };
}

const initialState = {
  showModalInvitations: false,
  showNewModal: false,
  nameUser: '',
  idTourney: 0,
  tournamentDetails: null as TournamentDetails | null,
  processingInvitation: false,
  detailsFetched: false,
  isFromLink: false, // Nueva propiedad para identificar si la invitación viene de un link
  skipFetchDetails: false, // Nueva propiedad para evitar que se ejecute fetchTournamentDetails
};

interface Props {
  pendingList: PendientesTda[];
  playingList: ParticipandoTda[];
  previousList: TorneoAnterior[];
  refetchFather: () => void;
  isMounted: boolean;
  isLastMatchDay?: boolean;
}

export const MainContent = React.memo(
  ({
    pendingList,
    playingList,
    previousList,
    refetchFather,
    isMounted,
    isLastMatchDay,
  }: Props) => {
    const navigate = useNavigate();
    const { isDesktop, isMobile } = useIsDesktop();
    const toast = useToast();
    const fetchedRef = useRef(false);

    const [state, setState] = useState(initialState);
    const [isAlreadyInvited, setIsAlreadyInvited] = useState(false);
    const dataPostLoginTkred =
      sessionStorage.getItem('postLoginTkred') || undefined;

    const listFiltered = useMemo(() => {
      const invitation: PendientesTda[] = [];
      const pending: PendientesTda[] = [];
      for (let i = 0; i < pendingList.length; i++) {
        const item = pendingList[i];

        if (item.rechazado) continue;

        if (!item.mostrarAccionPendienteAceptacion) {
          pending.push(item);
        } else {
          invitation.push(item);
        }
      }
      return { invitation, pending };
    }, [pendingList]);

    useEffect(() => {
      const handleInvitation = () => {
        const storedInvitationId = localStorage.getItem('invitationId');
        const storedInvitationUrl = localStorage.getItem('invitationUrl');
        const storedInvitationType = localStorage.getItem('invitationType');

        // Solo procesar si es una invitación de tipo torneo (no copa)
        if (
          storedInvitationId &&
          storedInvitationUrl &&
          (!storedInvitationType || storedInvitationType === 'torneo')
        ) {
          const invitationId = Number(storedInvitationId);

          // Verificar si ya estamos participando en este torneo
          const alreadyPlaying = playingList.some(
            (item) => item.id === invitationId
          );

          // Verificar si ya tenemos una invitación pendiente para este torneo
          // Verificamos en ambas listas de pendientes
          const alreadyInvited = listFiltered.invitation.some(
            (item) => item.id === invitationId
          );
          setIsAlreadyInvited(alreadyInvited);

          let alreadyPostulated = false;
          // Verificar si estamos postulados a ese torneo
          if (listFiltered.pending.length > 0) {
            alreadyPostulated = listFiltered.pending.some(
              (item) => item.id === invitationId
            );
          }

          if (alreadyPlaying || alreadyPostulated) {
            // Si ya estamos participando o postulados, limpiar el localStorage
            if (localStorage.getItem('invitationType') === 'torneo') {
              cleanStorageRedirectInvitation();
            }

            // Mostrar un mensaje informativo
            if (alreadyPlaying) {
              toast.show('Ya estás participando en este torneo');
            } else if (alreadyPostulated) {
              toast.show(
                'Ya estás postulado al torneo, actualmente estás a la espera de que te acepte el dueño.'
              );
            }

            // Asegurarnos de que el modal no se muestre
            setState(initialState);
          } else {
            // Solo mostrar el modal si no estamos ya participando o invitados
            setState((prevState) => ({
              ...prevState,
              showModalInvitations: true,
              idTourney: invitationId,
              isFromLink: true, // Marcar que esta invitación viene de un link
            }));
          }
        }
      };

      handleInvitation();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const cleanAfterInvitationUrl = () => {
      cleanStorageRedirectInvitation();
      setState(initialState);
    };

    useEffect(() => {
      const fetchTournamentDetails = async () => {
        if (
          state.showModalInvitations &&
          state.idTourney > 0 &&
          !fetchedRef.current &&
          !state.detailsFetched &&
          !state.skipFetchDetails &&
          state.isFromLink
        ) {
          fetchedRef.current = true;
          const txtError = 'El id del torneo es inválido';
          try {
            const response = await getTdaBasicDataById(state.idTourney);

            if (!response.estado) {
              console.error(txtError);
              toast.show(txtError);
              cleanAfterInvitationUrl();
              return;
            }

            setState((prevState) => ({
              ...prevState,
              tournamentDetails: response as TournamentDetails,
              nameUser:
                response.nombreDuenio
                  ?.concat(' ')
                  .concat(
                    response.apellidoDuenio ? response.apellidoDuenio : ''
                  ) ?? 'Alguien',
              showNewModal: true,
              detailsFetched: true,
              // Mantener el valor de isFromLink que ya teníamos
              isFromLink: prevState.isFromLink,
            }));
          } catch (error) {
            toast.show(txtError);
            cleanAfterInvitationUrl();
            throw new Error(txtError);
          }
        }
      };

      fetchTournamentDetails();
    }, [
      state.showModalInvitations,
      state.idTourney,
      toast,
      state.skipFetchDetails,
      state.isFromLink,
      state.detailsFetched,
    ]);

    useEffect(() => {
      if (!state.showModalInvitations) {
        fetchedRef.current = false;
      }
    }, [state.showModalInvitations]);

    const getTextParticipant = useCallback(
      (item: number | undefined) => {
        if (isDesktop) {
          if (item) {
            return `${formatearNumero(item)} ${
              item !== 1 ? 'participantes' : 'participante'
            }`;
          } else {
            return '1 participante';
          }
        } else {
          return undefined;
        }
      },
      [isDesktop]
    );

    const pendingDataList = useMemo<GenericItemTourneysData[] | null>(() => {
      if (pendingList.length === 0) return null;
      return pendingList
        .filter(
          ({ mostrarAccionPendienteAceptacion }) =>
            !mostrarAccionPendienteAceptacion
        )
        .map(
          ({ id, nombre, rechazado, ordenFecha, cantidadParticipantes }) => ({
            id,
            title: nombre,
            status: rechazado
              ? STATUS_TOURNEY_INVITATION.REJECTED
              : STATUS_TOURNEY_INVITATION.PENDING,
            secundaryText: `DESDE FECHA ${ordenFecha + 1}`,
            textCentered: getTextParticipant(cantidadParticipantes),
          })
        );
    }, [pendingList, getTextParticipant]);

    const previousDataList = useMemo((): GenericItemTourneysData[] | null => {
      if (previousList.length === 0) return null;
      return previousList.map((item: TorneoAnterior) => ({
        id: item.id,
        title: item.nombre,
        isVip: item.vip,
        disabled: isLastMatchDay,
      }));
    }, [isLastMatchDay, previousList]);

    const playingDataList = useMemo((): GenericItemTourneysData[] | null => {
      if (playingList.length === 0) return null;
      return playingList.map((item: ParticipandoTda) => ({
        id: item.id,
        title: item.nombre,
        // TODO: https://agea.atlassian.net/browse/DTA-9997 hasta tener diseños de txt + icon
        // isAdmin: item.owner,
        isVip: item.vip,
        secundaryText: `DESDE FECHA ${item.ordenFecha + 1}`,
        textCentered: getTextParticipant(item.cantidadParticipantes),
      }));
    }, [playingList, getTextParticipant]);

    const invitationDataList = useMemo((): GenericItemTourneysData[] | null => {
      const list = listFiltered.invitation;
      if (list.length === 0) return null;
      return list.map((item: PendientesTda) => {
        return {
          id: item.id,
          title: item.nombre,
          status: STATUS_TOURNEY_INVITATION.PENDING,
          subtitle: item.nombreDuenio,
          secundaryText: `${
            isDesktop ? `${item.nombreDuenio} - ` : ''
          }DESDE FECHA ${item.ordenFecha + 1}`,
          textCentered: getTextParticipant(item.cantidadParticipantes),
          disabled: isLastMatchDay,
        };
      });
    }, [getTextParticipant, isDesktop, isLastMatchDay, listFiltered]);

    const onHandleInvitations = async (action: ActionsTourneys) => {
      const dataUser = listFiltered.invitation.find(
        (tourney) => tourney.id === action.id
      );
      setState({
        ...initialState,
        showModalInvitations: true,
        nameUser: dataUser?.nombreDuenio ?? 'falta servicio',
        idTourney: action.id,
        detailsFetched: true,
        showNewModal: false,
        isFromLink: false,
        skipFetchDetails: true,
      });
      fetchedRef.current = true;
    };

    // Función para manejar invitaciones desde la lista "Te invitaron a participar"
    const onHandleListInvitation = async (
      action: ActionTypeManageTDAEnum,
      idTourney?: number
    ) => {
      const tourneyId = idTourney ?? state.idTourney;
      if (tourneyId <= 0 || state.processingInvitation) {
        return;
      }

      setState((prevState) => ({
        ...prevState,
        processingInvitation: true,
      }));

      try {
        // Para invitaciones en la lista "Te invitaron a participar"
        const responseManagePostulant = await postManagePostulant({
          actionType: action,
          idTourney: tourneyId,
        });

        if (responseManagePostulant.estado) {
          if (
            action === ActionTypeManageTDAEnum.AcceptInvitation &&
            isAlreadyInvited
          ) {
            toast.show(
              'Tenías una invitación pendiente para el torneo ' +
                state.tournamentDetails?.nombre +
                '. Ya estás participando.'
            );
          } else if (action === ActionTypeManageTDAEnum.AcceptInvitation) {
            toast.show('Gracias por aceptar la invitación.');
          } else if (action === ActionTypeManageTDAEnum.RejectInvitation) {
            // toast.show('Has rechazado la invitación');
          }
        } else {
          // Mostrar el mensaje específico del backend
          if (responseManagePostulant.mensaje) {
            toast.show(responseManagePostulant.mensaje);
          } else {
            toast.show(TEXTS.defaultError);
          }
        }
      } catch (error) {
        console.error(error);
        toast.show(TEXTS.defaultError);
      } finally {
        // Limpiar el estado y localStorage
        if (localStorage.getItem('invitationType') === 'torneo') {
          cleanStorageRedirectInvitation();
        }

        // Resetear completamente el estado
        setState(initialState);
        fetchedRef.current = false; // Resetear el ref
        // Refrescar los datos
        refetchFather();
      }
    };

    // Función para manejar invitaciones desde el modal (por link)
    const onHandleLinkInvitation = async (idTourney?: number) => {
      const tourneyId = idTourney ?? state.idTourney;
      if (tourneyId <= 0 || state.processingInvitation) {
        return;
      }

      setState((prevState) => ({
        ...prevState,
        processingInvitation: true,
      }));

      try {
        // Para invitaciones por link
        const responsePostulation = await requestPostulationTda(tourneyId);

        if (responsePostulation.estado) {
          toast.show(
            'Enviaste tu solicitud para unirte al Torneo de Amigos ' +
              state.tournamentDetails?.nombre +
              '.'
          );
        } else {
          // Mostrar el mensaje específico del backend
          if (responsePostulation.mensaje) {
            toast.show(responsePostulation.mensaje);
          } else {
            toast.show(TEXTS.defaultError);
          }
        }
      } catch (error) {
        console.error(error);
        toast.show(TEXTS.defaultError);
      } finally {
        // Limpiar el estado y localStorage
        if (localStorage.getItem('invitationType') === 'torneo') {
          cleanStorageRedirectInvitation();
        }

        // Resetear completamente el estado
        setState(initialState);
        fetchedRef.current = false; // Resetear el ref
        // Refrescar los datos
        refetchFather();
      }
    };

    const handleCloseModal = () => {
      if (localStorage.getItem('invitationType') === 'torneo') {
        cleanStorageRedirectInvitation();
      }
      setState(initialState);
      fetchedRef.current = false;
    };

    const { tourneyData, handleSetTourney } = useTourney();
    const onHandlePlaying = ({ id }: ActionsTourneys) => {
      const dataTourney = playingList.find((item) => item.id === id);

      if (dataTourney) {
        navigate(ROUTES.viewTourney);

        handleSetTourney({
          ...tourneyData,
          id,
          initDay: dataTourney.ordenFecha,
          isOwner: dataTourney.owner,
          isVip: dataTourney.vip,
        });
      }
    };

    const onHandlePrevious = ({ id }: ActionsTourneys) => {
      const dataTourney = previousList.find((item) => item.id === id);

      if (dataTourney) {
        navigate(ROUTES.reactivateFriendsTournament);

        handleSetTourney({
          ...tourneyData,
          tourney: dataTourney,
        });
      }
    };

    return (
      <MainHome
        className="w-full"
        subtitle="Jugá con tus amigos en un torneo privado y demostrales quién sabe más de fútbol."
        title="Torneos de Amigos Clásicos"
      >
        {isMounted && (
          <>
            {invitationDataList && (
              <TourneyList
                className="lg:mt-9 mt-6"
                context={EnumContextTourneys.MainContentHome}
                disabled={isLastMatchDay}
                handleSubmit={(params) => {
                  if (isMobile) {
                    params.action && onHandleInvitations(params);
                  } else {
                    onHandleListInvitation(
                      params.action === 'reject'
                        ? ActionTypeManageTDAEnum.RejectInvitation
                        : ActionTypeManageTDAEnum.AcceptInvitation,
                      params.id
                    );
                  }
                }}
                list={invitationDataList}
                paginator={isDesktop}
                title="Te invitaron a participar"
                variant="two-actions"
              />
            )}
            {playingDataList && (
              <TourneyList
                key={playingDataList.length}
                action={{
                  txtAction: 'Ver Torneo',
                  variantButton: 'secondary',
                }}
                className="lg:mt-9 mt-6"
                context={EnumContextTourneys.MainContentHome}
                contextGDT={EnumContextGDT.FriendTournament}
                disabled={isLastMatchDay}
                handleSubmit={onHandlePlaying}
                list={playingDataList}
                paginator={isDesktop}
                title="Estás participando"
              />
            )}
            {previousDataList && (
              <TourneyList
                action={{
                  txtAction: 'Reactivar',
                  variantButton: 'secondary',
                  txtActionDisabled: 'Reactivar',
                }}
                className="lg:mt-9 mt-6"
                context={EnumContextTourneys.MainContentHome}
                disabled={isLastMatchDay}
                handleSubmit={onHandlePrevious}
                list={previousDataList}
                paginator={isDesktop}
                title="Torneos edición pasada"
              />
            )}
            {pendingDataList && !isLastMatchDay && (
              <TourneyList
                className="lg:mt-9 mt-6"
                context={EnumContextTourneys.MainContentHome}
                list={pendingDataList}
                paginator={isDesktop}
                title="Esperando aceptación"
                variant="show-status"
              />
            )}
            {isDesktop && !pendingDataList && !playingDataList && (
              <MainBox>
                <p className="text-gdt-gray-2">
                  Todavía no estás participando en
                  <br /> ningún torneo.{' '}
                  <strong>
                    Creá uno nuevo o<br />
                    inscribite a uno de los recomendados.
                  </strong>
                </p>
                <Button
                  className="w-[187px] mt-8 mx-auto"
                  disabled={isLastMatchDay}
                  type="button"
                  variant="primary"
                  onClick={() => {
                    navigate(ROUTES.createFriendsTournament);
                  }}
                >
                  Crear nuevo torneo
                </Button>
              </MainBox>
            )}
            {/* Modal para invitaciones desde el listado) */}
            <Modal
              controlledOnClose={handleCloseModal}
              isControlledOpen={
                state.showModalInvitations &&
                isMobile &&
                !state.showNewModal &&
                state.idTourney > 0 &&
                !state.isFromLink &&
                !state.processingInvitation
              }
              render={() => {
                return (
                  <div className="text-center mt-2">
                    <p className="text-base font-medium">
                      <strong>{state.nameUser}</strong> te invitó a
                      <br />
                      participar de su Torneo de Amigos
                    </p>
                    <div className="flex space-x-2 my-10">
                      <Button
                        className="flex-1"
                        type="button"
                        variant="secondary"
                        onClick={() => {
                          onHandleListInvitation(
                            ActionTypeManageTDAEnum.RejectInvitation
                          );
                        }}
                      >
                        Rechazar
                      </Button>
                      <Button
                        className="flex-1"
                        type="button"
                        variant="primary"
                        onClick={() => {
                          onHandleListInvitation(
                            ActionTypeManageTDAEnum.AcceptInvitation
                          );
                        }}
                      >
                        Aceptar
                      </Button>
                    </div>
                  </div>
                );
              }}
              variant="controlled"
            />
            {/* Modal para invitaciones por link con detalles del torneo */}
            <Modal
              controlledOnClose={handleCloseModal}
              isControlledOpen={
                ((state.showModalInvitations && state.isFromLink) ||
                  (state.showNewModal && state.isFromLink)) &&
                state.idTourney > 0 &&
                !state.processingInvitation &&
                state.detailsFetched
              }
              render={() => (
                <div className="text-center mt-2">
                  <p className="text-base text-[#666666]">
                    <strong>{state.nameUser}</strong> te invitó a
                    <br />a postularte a su Torneo de Amigos
                  </p>
                  <div className="flex space-x-2 my-6">
                    <Button
                      className="flex-1 !bg-[#EEF2FF] !text-[#3366FF] hover:!bg-[#E5E9FF]"
                      type="button"
                      variant="secondary"
                      onClick={handleCloseModal}
                    >
                      Rechazar
                    </Button>
                    <Button
                      className="flex-1 !bg-[#3366FF] hover:!bg-[#2952CC]"
                      type="button"
                      variant="primary"
                      onClick={
                        isAlreadyInvited
                          ? () =>
                              onHandleListInvitation(
                                ActionTypeManageTDAEnum.AcceptInvitation,
                                state.idTourney
                              )
                          : () => onHandleLinkInvitation()
                      }
                    >
                      Aceptar
                    </Button>
                  </div>
                </div>
              )}
              variant="controlled"
            />

            {/* MODAL PARA INVITACIONES DESDE TKRED */}
            {dataPostLoginTkred && (
              <ModalTkred
                data={dataPostLoginTkred}
                handleRequestActions={onHandleListInvitation}
              />
            )}
          </>
        )}
      </MainHome>
    );
  }
);

MainContent.displayName = 'MainContent';
